/**
 * 브랜드 **표기 변형을 한 덩이로 묶는다** — 좁히기 칩이 같은 브랜드로 서너 개 서던 것을 없앤다.
 *
 * 색인의 `account` 는 판매몰이 적어 준 이름 그대로다. 그래서 한 브랜드가 이렇게 갈린다:
 * ```
 *   메디큐브 25  ·  메디큐브 (medicube) 5  ·  medicube 1     ← 같은 브랜드
 *   메디큐브 에이지알 12                                      ← 다른 라인(안 묶는다)
 * ```
 *
 * ## ⚠️ 별칭은 **데이터에서만** 얻는다 — 번역하지 않는다
 * 「메디큐브」와 「medicube」를 이어 주는 근거는 **괄호 표기**(`메디큐브 (medicube)`)뿐이다.
 * 그 한 줄이 두 이름을 같은 상품에 나란히 적어 두므로 별칭으로 삼을 수 있다.
 * 괄호 근거가 없으면 안 묶는다 — 한글·영문을 맞춰 보려면 사전이 필요하고, 그건 추측이다.
 *
 * ## ⚠️ 하위 라인은 안 묶는다
 * 「메디큐브 에이지알」은 「메디큐브」로 시작하지만 다른 제품 라인이다. **접두어로 묶지 않는다** —
 * 그러면 「닥터지」와 「닥터지 레드」가 한 칩이 되어 고를 수 없게 된다.
 *
 * ## ⚠️ 화면 이름은 **가장 많이 쓰인 표기**다
 * 묶은 뒤 어느 이름으로 보여 줄지는 개수가 정한다 — 사람이 실제로 많이 보는 표기다.
 */

/**
 * 괄호 안팎을 가른다. `메디큐브 (medicube)` → `{ outer: '메디큐브', inner: 'medicube' }`
 *
 * ⚠️ `outer` 는 **괄호 앞부분만**이다. 뒤에 붙은 말(`라운드랩（roundlab） 대용량` 의 「대용량」)까지
 * 합치면 브랜드가 아니라 상품 수식어가 섞인 이름이 되어 별칭이 안 이어진다.
 */
function splitParenthesizedAlias(name: string): { outer: string; inner: string } {
  const match = name.match(/^(.*?)[（(]([^）)]+)[）)]/);
  if (!match) return { outer: name, inner: '' };
  return { outer: match[1].trim(), inner: match[2].trim() };
}

/**
 * 비교용 열쇠 — 대소문자·공백·기호를 지운다.
 *
 * ⚠️ 한글은 그대로 둔다. 자모 분해 같은 것은 안 한다 — 「메디큐브」와 「메디 큐브」만 붙이면 된다.
 */
export function normalizeBrandKey(name: unknown): string {
  return String(name ?? '')
    .toLowerCase()
    .replace(/[\s._\-·&/]+/g, '')
    .trim();
}

/** 묶인 브랜드 한 덩이 */
export type BrandAliasGroup = {
  /** 그룹을 가리키는 값 — 화면 칩의 `value` 이자 조회에 쓰는 열쇠 */
  key: string;
  /** 화면에 적는 이름 — 가장 많이 쓰인 표기 */
  label: string;
  /** 색인에 실제로 들어 있는 이름 전부 — 조회는 이 목록으로 건다 */
  variants: string[];
  count: number;
};

/**
 * `{ 이름: 개수 }` → 묶인 그룹 목록(개수 내림차순).
 *
 * 묶는 규칙 둘뿐이다.
 * ① 정규화 열쇠가 같으면 같은 그룹 (`메디큐브` ↔ `메디 큐브`)
 * ② 괄호 별칭이 이어 주면 같은 그룹 (`메디큐브 (medicube)` 가 `메디큐브` 와 `medicube` 를 잇는다)
 */
export function buildBrandAliasGroups(counts: Map<string, number>): BrandAliasGroup[] {
  /** 열쇠 → 대표 열쇠 (union-find) */
  const parent = new Map<string, string>();
  const find = (k: string): string => {
    const seen = parent.get(k);
    if (seen == null || seen === k) return k;
    const root = find(seen);
    parent.set(k, root);
    return root;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  for (const name of counts.keys()) {
    const key = normalizeBrandKey(name);
    if (!key) continue;
    if (!parent.has(key)) parent.set(key, key);

    const { outer, inner } = splitParenthesizedAlias(name);
    if (!inner) continue;
    // 괄호가 있는 이름은 안팎 둘 다와 같은 그룹이다 — 그 한 줄이 별칭의 근거다
    for (const alias of [outer, inner]) {
      const aliasKey = normalizeBrandKey(alias);
      if (!aliasKey) continue;
      if (!parent.has(aliasKey)) parent.set(aliasKey, aliasKey);
      union(key, aliasKey);
    }
  }

  const groups = new Map<string, { variants: Map<string, number>; count: number }>();
  for (const [name, n] of counts) {
    const key = normalizeBrandKey(name);
    if (!key) continue;
    const root = find(key);
    const group = groups.get(root) ?? { variants: new Map<string, number>(), count: 0 };
    group.variants.set(name, (group.variants.get(name) ?? 0) + n);
    group.count += n;
    groups.set(root, group);
  }

  return [...groups.entries()]
    .map(([key, group]) => {
      const byCount = [...group.variants.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
      return {
        key,
        label: byCount[0][0],
        variants: byCount.map(([name]) => name),
        count: group.count,
      };
    })
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}
